// Make sure all of these start with "usc-section-"
import type { ReferenceType, VehicleType } from "../../shared-logic/types/UscCommonTypes";
import type { DfFuelTypeId } from "../../shared-logic/utils/dfConstants";
import { defaultMeasure, defaultReference, DfVehicleStatus } from "../../shared-logic/utils/dfConstants";

export enum ScrollTargetSelector {
    Highlights = "usc-section-highlights",
    Services = "usc-section-services",
    Accessories = "usc-section-accessories",
    CarDetails = "usc-section-cardetails",
    DealerInfo = "usc-section-dealerinfo",
}

// Make sure to align this map with the order of the pdpSections
export const scrollTargetIndexMap: Record<ScrollTargetSelector, number> = {
    [ScrollTargetSelector.Highlights]: 0,
    [ScrollTargetSelector.Services]: 1,
    [ScrollTargetSelector.Accessories]: 2,
    [ScrollTargetSelector.CarDetails]: 3,
    [ScrollTargetSelector.DealerInfo]: 4,
};

export enum PdpSection {
    Highlights = "highlights",
    Services = "services",
    Accessories = "accessories",
    CarDetails = "carDetails",
    DealerInfo = "dealerInfo",
}

export enum UscNavStepEnum {
    Pdp = "pdp",
    Extras = "extras",
}

export enum UscServicesType {
    Optional = "optional",
    Included = "included",
}

export enum SpecSection {
    Dimensions = "dimensions",
    // The consumption section contains much more than just consumption values
    // A better name would be eco as this contains consumption, emissions and electric ranges
    // We don't rename this section for now as this has to many impact on the configurations
    Consumption = "consumption",
    Engine = "engine",
    Performance = "performance",
    Transmission = "transmission",
    TyreInfo = "tyreInfo",
}

// This enum is mostly used for debugging purposes.
export enum UscFinanceDataSource {
    Df = "Data Foundation",
    Event = "Makolab finance calculator event",
    ApiUsed = "Makolab used finance API",
    ApiStock = "Makolab stock finance API",
}

export enum UscPdpFeeBehaviour {
    Always = "always", // Always show fees on pdp if external prices are enabled.
    Visited = "visited", // Only show fees if external prices are enabled and the user visited the PDP before or continued to the next step.
}

export enum DealerInfoType {
    PhoneNumber = "phoneNumber",
    Email = "email",
    Website = "website",
    Impressum = "impressum",
}

export const defaultVehicle: VehicleType = {
    id: "",
    vehicleStatus: { code: DfVehicleStatus.Available, description: "" },
    sourceCode: "",
    warranty: defaultReference,
    images: [],
    mileage: defaultMeasure,
    exteriorColour: "",
    interiorColour: "",
    trimColour: "",
    dealerId: "",
    dealerLocalId: "",
    isHubCar: false,
    history: {
        registrationDate: "",
        localRegistrationDate: "",
        events: [],
        previousAccident: null,
        previousOwners: null,
        previousUsage: null,
    },
    promotion: null,
    price: null,
    financingOffers: [],
    currencyCode: "",
    financeInfo: null,
    insuranceQuote: null,
    vehicleValue: { marketPrice: null, cataloguePrice: null, valuationDate: null, deliveryCost: null },
    licensePlate: "",
    vin: "",
    ucProgram: defaultReference,
    productionDate: "",
    ownershipTax: null,
    product: {
        brand: defaultReference,
        model: "",
        modelYear: "",
        localModelCode: "",
        bodyType: "",
        versionName: "",
        doors: 0,
        seats: 0,
        length: defaultMeasure,
        height: defaultMeasure,
        width: defaultMeasure,
        bootCapacity: defaultMeasure,
        grade: { gradeDescription: "" },
        carType: defaultReference,
        engine: {
            description: "",
            fuelType: defaultReference,
            marketingFuelType: defaultReference as ReferenceType<DfFuelTypeId>,
            displayFuelType: "",
            hybrid: false,
            plugin: false,
            cylinders: 0,
            powerOutputHorsepower: defaultMeasure,
            fiscalPower: defaultMeasure,
            displacement: defaultMeasure,
            engineKey: defaultReference,
        },
        performance: {
            maxSpeed: defaultMeasure,
            acceleration: defaultMeasure,
        },
        transmission: {
            name: "",
            gearbox: "",
        },
        eco: {
            label: defaultReference,
            euroClass: "",
            efficiencyClass: null,
            co2Emission: [],
            consumption: [],
        },
        equipment: [],
        externalProductAttributes: [],
        productVersionIdentifiers: [],
        ssn: "",
    },
    nextInspectionDate: "",
    isPledgedSale: null,
    externalVehicleAttributes: [],
    monthlyPrice: 0,
};

export type VehicleFetchReturnType = { vehicle: VehicleType; source?: unknown | null } | null; // source is used for debugging purposes
